import { useCallback, useState } from 'react';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { useMemberId } from '@trello/authentication';
import { Entitlements } from '@trello/entitlements';
import { sendErrorEvent } from '@trello/error-reporting';
import { intl } from '@trello/i18n';
import { forNamespace } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import { Checkbox } from '@trello/nachos/checkbox';
import { showFlag } from '@trello/nachos/experimental-flags';
import { CloseIcon } from '@trello/nachos/icons/close';
import { Popover, usePopover } from '@trello/nachos/popover';
import { Spinner } from '@trello/nachos/spinner';
import { useWorkspaceUserLimit } from '@trello/workspaces/user-limit';

import { useDeactivateMemberForWorkspaceMutation } from './DeactivateMemberForWorkspaceMutation.generated';
import { RefetchWorkspaceGuestsOnRemoveDocument } from './RefetchWorkspaceGuestsOnRemoveQuery.generated';
import { useRemoveMemberFromWorkspaceMutation } from './RemoveMemberFromWorkspaceMutation.generated';
import { useRemoveMemberWorkspaceFragment } from './RemoveMemberWorkspaceFragment.generated';
import { useTrelloRemoveMemberFromWorkspaceMutation } from './TrelloRemoveMemberFromWorkspaceMutation.generated';
import { useWorkspaceCollaboratorItemFragment } from './WorkspaceCollaboratorItemFragment.generated';
import { useWorkspaceMemberItemFragment } from './WorkspaceMemberItemFragment.generated';

import styles from './RemoveMemberFromWorkspaceButton.less';

const formatViewTitle = forNamespace('view title');

interface RemoveMemberFromWorkspaceButtonProps {
  idMember: string;
  idOrganization: string;
}

export function RemoveMemberFromWorkspaceButton({
  idMember,
  idOrganization,
}: RemoveMemberFromWorkspaceButtonProps) {
  const currentMemberId = useMemberId();

  const [
    isRemoveMemberFromAllBoardsChecked,
    setIsRemoveMemberFromAllBoardsChecked,
  ] = useState(true);
  const [isRequestPending, setIsRequestPending] = useState(false);

  const { collaboratorCount } = useWorkspaceUserLimit({
    workspaceId: idOrganization,
  });

  const { triggerRef, toggle, hide, popoverProps } = usePopover({
    onShow: useCallback(() => {
      Analytics.sendScreenEvent({
        name: 'removeMemberFromWorkspaceInlineDialog',
        containers: formatContainers({ idOrganization }),
        attributes: { collaboratorCount },
      });
    }, [collaboratorCount, idOrganization]),
  });

  const [removeMemberFromWorkspaceAndBoards] =
    useTrelloRemoveMemberFromWorkspaceMutation({
      refetchQueries: [
        {
          query: RefetchWorkspaceGuestsOnRemoveDocument,
          variables: { workspaceId: idOrganization },
          context: {
            operationName: 'RefetchWorkspaceGuestsOnRemoveDocument',
            document: RefetchWorkspaceGuestsOnRemoveDocument,
          },
        },
      ],
    });

  const [removeMemberFromWorkspace] = useRemoveMemberFromWorkspaceMutation({
    refetchQueries: [
      {
        query: RefetchWorkspaceGuestsOnRemoveDocument,
        variables: { workspaceId: idOrganization },
        context: {
          operationName: 'RefetchWorkspaceGuestsOnRemoveDocument',
          document: RefetchWorkspaceGuestsOnRemoveDocument,
        },
      },
    ],
  });

  const [deactivateMember] = useDeactivateMemberForWorkspaceMutation();

  const { data: collaborator } = useWorkspaceCollaboratorItemFragment({
    from: { id: idMember },
    optimistic: true,
  });

  const { data: member } = useWorkspaceMemberItemFragment({
    from: { id: idMember },
    optimistic: true,
  });

  const { data: organization } = useRemoveMemberWorkspaceFragment({
    from: { id: idOrganization },
    optimistic: true,
  });

  const isCurrentMemberEnterpriseAdmin =
    organization?.enterprise?.idAdmins?.includes(currentMemberId);

  const isCurrentMemberWorkspaceAdmin =
    organization?.memberships?.find(
      (membership) => membership.idMember === currentMemberId,
    )?.memberType === 'admin';

  const membership = organization?.memberships?.find(
    (mship) => mship.idMember === idMember,
  );

  const isDeactivated = membership?.deactivated;
  const isUnconfirmed = membership?.unconfirmed;
  const isLastWorkspaceAdmin =
    membership?.memberType === 'admin' &&
    organization?.memberships?.filter((mship) => mship.memberType === 'admin')
      ?.length === 1;

  const isGuest = Boolean(!membership);
  const isPaidOrg =
    organization?.offering && !Entitlements.isFree(organization?.offering);
  const canDeactivate =
    !isGuest &&
    isPaidOrg &&
    (isCurrentMemberWorkspaceAdmin || isCurrentMemberEnterpriseAdmin) &&
    !isUnconfirmed &&
    !isDeactivated;
  const fullName = collaborator?.fullName || member?.fullName;

  const onRemove = useCallback(async () => {
    hide();
    setIsRequestPending(true);
    const traceId = Analytics.startTask({
      taskName: 'edit-organization/members/remove',
      source: 'removeMemberFromWorkspaceInlineDialog',
    });

    try {
      if (isGuest || isRemoveMemberFromAllBoardsChecked) {
        const response = await removeMemberFromWorkspaceAndBoards({
          variables: {
            idMember: `ari:cloud:trello::user/${idMember}`,
            idOrganization: `ari:cloud:trello::workspace/${idOrganization}`,
          },
          context: { traceId },
        });

        const mutationResult =
          response?.data?.trello?.removeMemberFromWorkspace;
        if (mutationResult && !mutationResult.success) {
          throw mutationResult.errors;
        }
      } else {
        await removeMemberFromWorkspace({
          variables: { member: { id: idMember }, idOrganization, traceId },
        });
      }

      showFlag({
        id: 'remove-guest-from-team-boards',
        title: intl.formatMessage(
          {
            id: 'templates.org_member_activity.remove-member-from-workspace-success',
            defaultMessage: '{name} was removed from {workspaceName}.',
            description: '{name} was removed from {workspaceName}.',
          },
          {
            name: fullName,
            workspaceName: organization?.displayName,
          },
        ),
        appearance: 'success',
        isAutoDismiss: true,
        msTimeout: 5000,
      });

      Analytics.taskSucceeded({
        taskName: 'edit-organization/members/remove',
        source: 'removeMemberFromWorkspaceInlineDialog',
        traceId,
      });

      Analytics.sendTrackEvent({
        action: 'removed',
        actionSubject: 'member',
        source: 'removeMemberFromWorkspaceInlineDialog',
        containers: formatContainers({ idOrganization }),
        attributes: { collaboratorCount, isGuest },
      });
    } catch (error) {
      setIsRequestPending(false);
      showFlag({
        id: 'remove-guest-from-team-boards',
        title: intl.formatMessage(
          {
            id: 'templates.org_member_activity.remove-member-from-workspace-error',
            defaultMessage: 'Couldn’t remove {name}. Try again.',
            description: 'Couldn’t remove {name}. Try again.',
          },
          {
            name: fullName,
          },
        ),
        appearance: 'error',
        isAutoDismiss: true,
        msTimeout: 5000,
      });

      Analytics.taskFailed({
        taskName: 'edit-organization/members/remove',
        source: 'removeMemberFromWorkspaceInlineDialog',
        traceId,
        error,
      });

      sendErrorEvent(error, {
        tags: {
          ownershipArea: 'trello-web-eng',
        },
        extraData: {
          component: 'RemoveMemberFromWorkspaceButton',
        },
      });
    }
  }, [
    collaboratorCount,
    fullName,
    hide,
    idMember,
    idOrganization,
    isGuest,
    isRemoveMemberFromAllBoardsChecked,
    organization?.displayName,
    removeMemberFromWorkspace,
    removeMemberFromWorkspaceAndBoards,
  ]);

  const onChangeCheckbox = useCallback(() => {
    setIsRemoveMemberFromAllBoardsChecked(!isRemoveMemberFromAllBoardsChecked);
  }, [
    isRemoveMemberFromAllBoardsChecked,
    setIsRemoveMemberFromAllBoardsChecked,
  ]);

  const onDeactivate = useCallback(async () => {
    hide();
    setIsRequestPending(true);
    const traceId = Analytics.startTask({
      taskName: 'deactivate-member/organization',
      source: 'workspaceMembersScreen',
    });

    try {
      await deactivateMember({
        variables: { memberId: idMember, workspaceId: idOrganization, traceId },
      });

      Analytics.taskSucceeded({
        source: 'workspaceMembersScreen',
        taskName: 'deactivate-member/organization',
        traceId,
      });

      Analytics.sendTrackEvent({
        action: 'deactivated',
        actionSubject: 'member',
        source: 'workspaceMembersScreen',
        containers: formatContainers({ idOrganization }),
        attributes: { taskId: traceId },
      });
    } catch (error) {
      console.error('Error deactivating member', error);

      showFlag({
        id: 'deactivate-member',
        title: intl.formatMessage(
          {
            id: 'templates.org_member_activity.deactivate-member-in-workspace-error',
            defaultMessage: 'Could not deactivate {name}. Try again.',
            description: 'Could not deactivate {name}. Try again.',
          },
          {
            name: fullName,
          },
        ),
        appearance: 'error',
        isAutoDismiss: true,
        msTimeout: 5000,
      });

      Analytics.taskFailed({
        error,
        source: 'workspaceMembersScreen',
        taskName: 'deactivate-member/organization',
        traceId,
      });

      sendErrorEvent(error, {
        tags: {
          ownershipArea: 'trello-web-eng',
        },
        extraData: {
          component: 'RemoveMemberFromWorkspaceButton',
        },
      });
    }

    setIsRequestPending(false);
  }, [deactivateMember, fullName, hide, idMember, idOrganization]);

  let popoverTitle;
  if (isGuest) {
    popoverTitle = intl.formatMessage({
      id: 'templates.org_remove_or_deactivate_member.remove-guest-popover-title',
      defaultMessage: 'Remove guest',
      description:
        "Title of popover for removing a guest's access to the Workspace",
    });
  } else {
    popoverTitle = isPaidOrg
      ? formatViewTitle('remove or deactivate member')
      : formatViewTitle('remove member');
  }

  const popoverActionDescription = isGuest
    ? intl.formatMessage({
        id: 'templates.org_remove_or_deactivate_member.remove-all-access-guest-popover-content',
        defaultMessage:
          'Remove all access to the Workspace. They will receive a notification.',
        description:
          "Description of removing a guest's access to the Workspace",
      })
    : intl.formatMessage({
        id: 'templates.org_remove_or_deactivate_member.remove-all-access-to-the-organization-the-member-will-remain-on-all-their-boards-in-this-organization-they-will-receive-a-notification',
        defaultMessage:
          'Remove all access to the Workspace. The member will remain on all their boards in this Workspace. They will receive a notification.',
        description:
          "Description of removing a members's access to the Workspace",
      });

  return (
    <>
      <Button
        className={styles.removeMemberFromWorkspaceButton}
        iconBefore={isRequestPending ? undefined : <CloseIcon />}
        onClick={toggle}
        ref={triggerRef}
        isDisabled={isRequestPending || isLastWorkspaceAdmin}
      >
        {isRequestPending ? (
          <Spinner inline small />
        ) : (
          intl.formatMessage({
            id: 'templates.member_list_item_detail.remove-ellipsis',
            defaultMessage: 'Remove…',
            description: "Button that removes user's access to the Workspace",
          })
        )}
      </Button>
      <Popover {...popoverProps} title={popoverTitle}>
        <a
          className={styles.removeMemberFromWorkspacePopoverContent}
          href="#"
          onClick={onRemove}
        >
          <p>
            {intl.formatMessage({
              id: 'templates.org_remove_or_deactivate_member.remove-from-organization',
              defaultMessage: 'Remove from Workspace',
              description:
                "Heading for description of removing a user's access to the Workspace",
            })}
          </p>
          <p className={styles.removeMemberFromWorkspaceDescription}>
            {popoverActionDescription}
          </p>
        </a>
        {!isGuest && (
          <Checkbox
            className={styles.removeMemberCheckbox}
            defaultChecked={true}
            label={intl.formatMessage({
              id: 'templates.org_remove_or_deactivate_member.check-this-box-to-remove-the-member-from-all-boards',
              defaultMessage:
                'Check this box to remove the member from all boards.',
              description:
                "Label for checkbox controlling a member's Workspace board access following removal from the Workspace",
            })}
            onChange={onChangeCheckbox}
          />
        )}
        {canDeactivate && (
          <a
            className={styles.removeMemberFromWorkspacePopoverContent}
            href="#"
            onClick={onDeactivate}
          >
            <p>
              {intl.formatMessage({
                id: 'templates.org_remove_or_deactivate_member.deactivate',
                defaultMessage: 'Deactivate',
                description:
                  'Heading for option to deactivate a Workspace member',
              })}
            </p>
            <p className={styles.removeMemberFromWorkspaceDescription}>
              {intl.formatMessage({
                id: 'templates.org_remove_or_deactivate_member.disable-member-access',
                defaultMessage:
                  "Disable member's access to Workspace boards, but allow other Workspace members to see what cards and boards the member was on.",
                description: 'Description of member deactivation',
              })}
            </p>
          </a>
        )}
      </Popover>
    </>
  );
}
